import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Banknote, Clock, MapPin } from "lucide-react";
import { useTenant } from "../../context/TenantContext";
import { useAuth } from "../../context/AuthContext";
import { GET_DAILY_MENU_URL } from "../../features/UrlHelper";
import { ALLERGENS, ITEM_TYPE_META } from "../../features/orderConstants";
import { compareMenuCodesNatural } from "../../features/orderUtils";
import "./HomePage.scss";

// Kroky sa lisia podla toho, ci prevadzka berie objednavky aj cez web (R41).
// Keby tam ostalo "Zavolajte na pobocku" aj so zapnutym objednavanim, posielali
// by sme ludi telefonovat hned vedla tlacidla, ktore to spravi za nich.
// Prve dva kroky maju odkaz na miesto, kde sa dany krok naozaj robi — sekcia
// tak nie je len popis, ale aj rozcestnik.
const KROKY_TELEFON = [
  { title: "Vyberte si", desc: "Prezrite si denné menu a vyberte si jedlo, ktoré vám chutí.", to: "/denne-menu", odkaz: "Denné menu" },
  { title: "Zavolajte", desc: "Objednávku prijmeme telefonicky a potvrdíme za pár sekúnd.", to: "/kontakt", odkaz: "Kontakt" },
  { title: "Prevezmite", desc: "Jedlo doručíme až k vašim dverám." },
];

const KROKY_ONLINE = [
  { title: "Vyberte si", desc: "Dnešná ponuka je tu stále aktuálna, aj s cenami, ktoré platia pre vás.", to: "/denne-menu", odkaz: "Denné menu" },
  { title: "Objednajte", desc: "Odošlete objednávku priamo tu. Alebo zavolajte, ak vám to tak vyhovuje.", to: "/objednavka", odkaz: "Objednať" },
  { title: "Prevezmite", desc: "Jedlo vám dovezieme, alebo si ho vyzdvihnete na prevádzke." },
];

/** Dnešok v slovenskom čase — server menu adresuje dátumom, nie „dnes“. */
const dnesIso = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Bratislava" }).format(new Date());

/**
 * Skupina (polievky, hlavné jedlá…), v nej kód prirodzene: A, B, C a 1, 2 … 10.
 * Server poradie v rámci typu nezaručuje. Jedlo bez kódu ide na koniec
 * skupiny — prázdny reťazec by ho inak postavil pred „A“.
 */
const poradiePonuky = (a, b) =>
  (ITEM_TYPE_META[a.item_type]?.order ?? 99) - (ITEM_TYPE_META[b.item_type]?.order ?? 99) ||
  Number(!a.menu_code) - Number(!b.menu_code) ||
  compareMenuCodesNatural(a.menu_code, b.menu_code);

const eur = (v) => `${Number(v || 0).toFixed(2)} €`;

const NAZVY_ALERGENOV = Object.fromEntries(ALLERGENS.map((a) => [String(a.id), a.label]));

/** "1,3,9" → ["1", "3", "9"] — server ich drží ako text oddelený čiarkami. */
const alergeny = (zoznam) =>
  String(zoznam || "").split(",").map((a) => a.trim()).filter(Boolean);

function HomePage() {
  // Text a video boli natvrdo Veteranove. Teraz ich urcuje branding prevadzky
  // (companies.hero_*), fallbacky su v features/tenantDefaults.js.
  const { branding, tenant } = useTenant();
  const { isLoggedIn } = useAuth();
  const objednavanie = tenant?.onlineOrdersEnabled === true;
  const kroky = objednavanie ? KROKY_ONLINE : KROKY_TELEFON;
  // hero_title moze mat viac riadkov ("Jedlo z Veterán Pubu\npriamo k vašim dverám.")
  const riadkyNadpisu = String(branding.heroTitle).split("\n");

  /*
   * Dnešná ponuka priamo na úvodnej stránke.
   *
   * PREČO: človek, ktorý sem príde, chce vedieť jedno — čo je dnes na obed.
   * Tri obrázkové dlaždice s heslami mu to nepovedia a vyzerajú ako na každom
   * druhom webe. Toto je skutočný obsah prevádzky, nie výplň.
   *
   * Keď menu nie je zverejnené, sekcia sa nekreslí vôbec — prázdny rám
   * s nadpisom „Dnes v ponuke“ by bol horší než žiadny.
   */
  const [ponuka, setPonuka] = useState([]);

  useEffect(() => {
    let zrusene = false;
    const dnes = dnesIso();
    axios
      .get(GET_DAILY_MENU_URL({ date_from: dnes, date_to: dnes }))
      .then((res) => {
        if (zrusene) return;
        const polozky = Array.isArray(res.data) ? res.data : (res.data?.items || []);
        setPonuka(polozky.filter((p) => !p.x__packed).sort(poradiePonuky));
      })
      // Ponuka je doplnok, nie podmienka — bez nej stránka funguje ďalej.
      .catch(() => { if (!zrusene) setPonuka([]); });
    return () => { zrusene = true; };
  }, []);

  const maVizual = Boolean(branding.heroVideoUrl || branding.heroImageUrl);

  // Položky po skupinách (polievky, hlavné jedlá…). Skupiny idú pod seba a až
  // položky v nich do dvoch stĺpcov — keby boli skupiny vedľa seba, hlavných
  // jedál je vždy viac než polievok a stĺpec s nimi by bol oveľa vyšší.
  // Poradie skupín drží už zoradená `ponuka`.
  const skupiny = [];
  for (const p of ponuka) {
    const posledna = skupiny[skupiny.length - 1];
    if (posledna?.typ === p.item_type) posledna.polozky.push(p);
    else skupiny.push({ typ: p.item_type, polozky: [p] });
  }

  return (
    <div className="home">
      {/* ── HERO ── */}
      {/* Hlavička a telo hero sú samostatné bloky, aby sa vizuál pod 1100px
          dal vložiť medzi nadpis a tlačidlá (grid-template-areas v SCSS). */}
      <section className="home__hero">
        <div className="home__hero-head">
          <span className="home__eyebrow">
            {objednavanie ? "Objednávky online aj telefonicky" : "Denné menu s rozvozom"}
          </span>
          <h1 className="home__hero-title">
            {riadkyNadpisu.map((riadok, i) => (
              <span key={i} className={i > 0 ? "home__hero-title-akcent" : undefined}>
                {riadok}
              </span>
            ))}
          </h1>
          {branding.heroSubtitle && <p className="home__hero-subtitle">{branding.heroSubtitle}</p>}
        </div>

        <div className="home__hero-body">
          <div className="home__hero-cta">
            <Link
              to={objednavanie ? "/objednavka" : "/denne-menu"}
              className="home__btn home__btn--primary"
            >
              {objednavanie ? "Objednať teraz" : "Dnešné menu"} <ArrowRight size={18} />
            </Link>
            <Link to={objednavanie ? "/denne-menu" : "/kontakt"} className="home__btn home__btn--outline">
              {objednavanie ? "Pozrieť menu" : "Kontakt"}
            </Link>
          </div>

          <ul className="home__trust">
            <li><Clock size={16} aria-hidden="true" /> Čerstvé denné menu</li>
            <li><MapPin size={16} aria-hidden="true" /> Rozvoz až k dverám</li>
            <li><Banknote size={16} aria-hidden="true" /> Platba pri prevzatí</li>
          </ul>
        </div>

        {/* Vizuál je v ráme, nie na celé pozadie: pod textom cez celú šírku
            bol rozmazaný a text na ňom sa čítal ťažko. */}
        {maVizual && (
          <div className="home__hero-media">
            {branding.heroVideoUrl ? (
              <video
                className="home__hero-video"
                src={branding.heroVideoUrl}
                autoPlay
                muted
                loop
                playsInline
              />
            ) : (
              <img className="home__hero-video" src={branding.heroImageUrl} alt="" />
            )}
          </div>
        )}
      </section>

      {/* ── DNES V PONUKE ── */}
      {ponuka.length > 0 && (
        <section className="home__dnes">
          <div className="home__container">
            <div className="home__dnes-hlavicka">
              <div>
                <p className="home__eyebrow">Dnes v ponuke</p>
                <h2 className="home__section-title">Čo máme dnes na obed</h2>
              </div>
              <Link to="/denne-menu" className="home__odkaz">
                Celé menu na týždeň <ArrowRight size={16} />
              </Link>
            </div>

            <div className="home__dnes-karta">
              {skupiny.map(({ typ, polozky }) => {
                const meta = ITEM_TYPE_META[typ];
                const Ikona = meta?.Icon;
                // Stĺpce sa čítajú zhora nadol: prvá polovica vľavo, zvyšok
                // vpravo (pri nepárnom počte má ľavý o jedno viac). Grid dostane
                // počet riadkov a plní sa po stĺpcoch — DOM ostáva v poradí
                // 1, 2, 3…, takže čítačka aj mobil (jeden stĺpec) idú správne.
                const riadkov = Math.ceil(polozky.length / 2);
                return (
                  <section key={typ} className="home__dnes-skupina">
                    <h3 className="home__dnes-typ">
                      {Ikona && (
                        <span className="home__dnes-typ-ikona" aria-hidden="true">
                          <Ikona size={18} />
                        </span>
                      )}
                      {meta?.label || "Ďalšie"}
                    </h3>
                    <ul className="home__dnes-zoznam" style={{ "--riadky": riadkov }}>
                      {polozky.map((p, i) => (
                        <li
                          key={p.id}
                          className={`home__dnes-polozka${i === 0 || i === riadkov ? " home__dnes-polozka--hore" : ""}`}
                        >
                          {p.menu_code && <span className="home__dnes-kod">{p.menu_code}</span>}
                          <div className="home__dnes-text">
                            <span className="home__dnes-nazov">{p.name}</span>
                            {(p.description || p.allergens_list) && (
                              <span className="home__dnes-detail">
                                {p.description && <span>{p.description}</span>}
                                {alergeny(p.allergens_list).length > 0 && (
                                  <span>
                                    Alergény:{" "}
                                    {alergeny(p.allergens_list).map((a, i) => (
                                      <span key={a} title={NAZVY_ALERGENOV[a]}>
                                        {i > 0 && ", "}{a}
                                      </span>
                                    ))}
                                  </span>
                                )}
                              </span>
                            )}
                          </div>
                          {p.price != null && <span className="home__dnes-cena">{eur(p.price)}</span>}
                        </li>
                      ))}
                    </ul>
                  </section>
                );
              })}
              <p className="home__dnes-poznamka">
                Čísla alergénov podľa nariadenia EÚ 1169/2011 — ich zoznam nájdete
                v <Link to="/denne-menu">dennom menu</Link>.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* ── AKO TO FUNGUJE ── */}
      {/* Nadpis vľavo, kroky vpravo ako očíslovaný zoznam na jednej osi —
          nie tri vycentrované stĺpce s veľkými bledými číslami. */}
      <section className="home__how">
        <div className="home__container home__how-inner">
          <div className="home__how-hlavicka">
            <p className="home__eyebrow">Ako to funguje</p>
            <h2 className="home__section-title">Tri kroky k obedu</h2>
            <p className="home__how-lede">
              {objednavanie
                ? "Objednať môžete cez web aj telefonicky. Platíte až pri prevzatí."
                : "Objednávky prijímame telefonicky. Platíte až pri prevzatí."}
            </p>
          </div>

          <ol className="home__steps">
            {kroky.map((s, i) => (
              <li key={s.title} className="home__step">
                <span className="home__step-num" aria-hidden="true">{i + 1}</span>
                <div className="home__step-text">
                  <h3 className="home__step-title">{s.title}</h3>
                  <p className="home__step-desc">{s.desc}</p>
                  {s.to && (
                    <Link to={s.to} className="home__odkaz home__step-odkaz">
                      {s.odkaz} <ArrowRight size={16} />
                    </Link>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── ZÁVEREČNÁ VÝZVA ── */}
      <section className="home__cta">
        <div className="home__container home__cta-inner">
          {objednavanie ? (
            <>
              <h2 className="home__cta-title">Dáte si dnes obed?</h2>
              <p className="home__cta-text">
                Vyberiete si, potvrdíte a máte vybavené. Ak u nás ešte účet nemáte,
                založíte si ho za chvíľu.
              </p>
              <div className="home__cta-actions">
                <Link to="/objednavka" className="home__btn home__btn--white">
                  Objednať teraz <ArrowRight size={18} />
                </Link>
                {!isLoggedIn && (
                  <Link to="/registracia" className="home__btn home__btn--outline-light">
                    Vytvoriť účet
                  </Link>
                )}
              </div>
            </>
          ) : (
            <>
              <h2 className="home__cta-title">Objednajte si telefonicky</h2>
              <p className="home__cta-text">
                Pozrite si dnešnú ponuku a zavolajte nám — objednávku vybavíme hneď.
              </p>
              <div className="home__cta-actions">
                <Link to="/denne-menu" className="home__btn home__btn--white">
                  Dnešné menu <ArrowRight size={18} />
                </Link>
                <Link to="/kontakt" className="home__btn home__btn--outline-light">
                  Kontakt
                </Link>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

export default HomePage;
